export type CommonType = number | string | moment.Moment | undefined | null;

export interface LabelledValue {
  value: any;
  label: string;
}

export const isLabelledValue = (type: any): type is LabelledValue => {
  return type != null && typeof type === 'object' && type.hasOwnProperty('label');
}

export const hasValue = (type: any): type is { value: any } => {
  return type != null && typeof type === 'object' && type.hasOwnProperty('value');
}

export const compare = (a: CommonType, b: CommonType, { descending } = { descending: false }): number => {
  const sortOrder = descending ? -1 : 1;
  if (a == null && b == null) {
    return 0;
  }
  if (a == null && b != null) {
    return -1 * sortOrder;
  }
  if (a != null && b == null) {
    return 1 * sortOrder;
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * sortOrder;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b) * sortOrder;
  }

  if (moment.isMoment(a) && moment.isMoment(b)) {
    return a.isSame(b)
      ? 0
      : (a.isAfter(b) ? 1 : -1) * sortOrder;
  }

  throw new Error('Cannot compare values');
}

export const compareByLabel = (a: Partial<LabelledValue>, b: Partial<LabelledValue>, { descending } = { descending: false }): number => {
  if (isLabelledValue(a) && isLabelledValue(b)) {
    return compare(a.label, b.label, { descending });
  }
  return compare(a as any, b as any, { descending });
}

export const compareByValue = (a: any, b: any, { descending } = { descending: false }): number => {
  if (hasValue(a) && hasValue(b)) {
    return compare(a.value, b.value, { descending });
  }
  if (hasValue(a) && !hasValue(b)) {
    return compare(a.value, b, { descending });
  }
  if (!hasValue(a) && hasValue(b)) {
    return compare(a, b.value, { descending });
  }
  return compare(a, b, { descending });
}

export const partialMatches = (a: string | number | LabelledValue,
                               b: string | number | LabelledValue,
                               { ignoreCase }: { ignoreCase: boolean }) => {
  if (a == null) {
    return false;
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a === b;
  }

  let lhs = isLabelledValue(a) ? a.label : a.toString();
  let rhs = isLabelledValue(b) ? b.label : b.toString();
  if (ignoreCase) {
    lhs = lhs.toLocaleLowerCase();
    rhs = rhs.toLocaleLowerCase();
  }

  return lhs.indexOf(rhs) !== -1;
}
